'use client';

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { cn } from '@/lib/utils';
import type { CalendarResponse, CalendarDay, DateInterval } from '@/types/calendar';
import {
  compareDates,
  isDateInRange,
  isDateBetween,
  getDatesBetween,
  getMonthKeyFromParts,
  addOneDay,
  addDays,
} from '@/lib/calendar-utils';
import { computeBestRate } from '@/lib/calendar/helpers';
import { MinStayNotice } from './min-stay-notice';
import { log } from '@/lib/logger';

interface AvailabilityCalendarProps {
  roomSlug: string;
  initialData: CalendarResponse;
  interval: DateInterval;
  onIntervalChange: (interval: DateInterval) => void;
  onCalendarDataChange?: (days: CalendarDay[]) => void;
  onPricesLoaded?: (minPrice: number | null, currency: string) => void;
  onError?: () => void;
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

/**
 * Client-side fallback for the "starting from" price when the server
 * didn't emit `minPrice`. Drops the `available` filter — the rate-card
 * price is independent of per-day availability, so a fully booked
 * month can still advertise its rate.
 */
function deriveMinPrice(days: CalendarDay[]): number | null {
  const prices = days
    .filter((d) => !d.isPast && d.price !== null)
    .map((d) => d.price as number);
  return prices.length > 0 ? Math.min(...prices) : null;
}

export function AvailabilityCalendar({
  roomSlug,
  initialData,
  interval,
  onIntervalChange,
  onCalendarDataChange,
  onPricesLoaded,
  onError,
}: AvailabilityCalendarProps) {
  const t = useTranslations('common');

  const [currentYear, setCurrentYear] = useState(
    initialData.data?.year ?? new Date().getFullYear()
  );
  const [currentMonth, setCurrentMonth] = useState(
    initialData.data?.month ?? new Date().getMonth() + 1
  );
  const [calendarData, setCalendarData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);

  // Selection phase tracks the 3-click cycle on top of the 1-night-default:
  //   'idle'        — no selection
  //   'one-night'   — a fresh 1-night default is in place; next click *may* extend
  //   'multi-night' — user has committed (extended or URL-seeded); next click restarts
  type SelectionPhase = 'idle' | 'one-night' | 'multi-night';
  const [phase, setPhase] = useState<SelectionPhase>(() => {
    if (interval.checkIn && interval.checkOut) return 'multi-night';
    if (interval.checkIn) return 'one-night';
    return 'idle';
  });

  // External clears (parent's clear-dates / store reset) snap phase back to idle.
  useEffect(() => {
    if (!interval.checkIn && !interval.checkOut) setPhase('idle');
  }, [interval.checkIn, interval.checkOut]);

  // Hover preview only renders during the "one extend chance" window.
  const selectionMode: 'check-in' | 'check-out' =
    phase === 'one-night' ? 'check-out' : 'check-in';

  // Hovered date for preview during check-out selection
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  // Cache of month data for cross-month validation. Storing minPrice
  // alongside days lets us preserve the server's next-month fallback
  // when a month is hit again from the cross-month cache.
  const [monthDataCache, setMonthDataCache] = useState<Map<string, { days: CalendarDay[]; minPrice: number | null }>>(() => {
    const initial = new Map<string, { days: CalendarDay[]; minPrice: number | null }>();
    if (initialData.data) {
      const key = getMonthKeyFromParts(initialData.data.year, initialData.data.month);
      initial.set(key, { days: initialData.data.days, minPrice: initialData.data.minPrice ?? null });
    }
    return initial;
  });

  // Calculate best rate price and check if prices vary (festive days excluded
  // — see computeBestRate: a pricey holiday must not green the rest of the month).
  const { bestRatePrice, hasPriceVariation } = useMemo(
    () => computeBestRate(calendarData.data?.days ?? []),
    [calendarData]
  );

  // Check if a day has the best rate
  const isBestRate = useCallback(
    (day: CalendarDay): boolean => {
      return (
        hasPriceVariation &&
        day.available &&
        !day.isPast &&
        day.price !== null &&
        bestRatePrice !== null &&
        day.price === bestRatePrice
      );
    },
    [bestRatePrice, hasPriceVariation]
  );

  // Find day data across cached months
  const findDayData = useCallback(
    (dateStr: string): CalendarDay | undefined => {
      const monthKey = dateStr.substring(0, 7); // "YYYY-MM"
      const monthDays = monthDataCache.get(monthKey)?.days;
      return monthDays?.find((d) => d.date === dateStr);
    },
    [monthDataCache]
  );

  // Flash state for blocked dates (visual feedback when range is invalid)
  const [flashDates, setFlashDates] = useState<Set<string>>(new Set());
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Find unavailable dates in a range.
  // A night we have NO data for (a month the client hasn't downloaded yet) is
  // treated as blocked — "cannot verify" must fail safe, not assume available
  // (docs/82 · A7). Otherwise a stay extended into an unloaded month could
  // silently include a booked night, surfacing only when PYN rejects it at
  // submit. In the normal flow the displayed month is always loaded, so this
  // only bites genuine cross-into-unloaded-month selections.
  const getBlockedDatesInRange = useCallback(
    (checkIn: string, checkOut: string): string[] => {
      const datesInRange = getDatesBetween(checkIn, checkOut);
      return datesInRange.filter((date) => {
        const dayData = findDayData(date);
        return !dayData || dayData.available === false;
      });
    },
    [findDayData]
  );

  // Check if we can navigate to previous month
  const canNavigateToPrevious = useMemo(() => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1;
    return !(
      currentYear < todayYear ||
      (currentYear === todayYear && currentMonth <= todayMonth)
    );
  }, [currentYear, currentMonth]);

  // AbortController for cancelling pending fetches on rapid navigation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch calendar data for a month (does NOT set year/month — caller does that)
  const fetchCalendarData = useCallback(
    async (year: number, month: number) => {
      // Check client-side cache first — instant on back-navigation
      const cacheKey = getMonthKeyFromParts(year, month);
      const cached = monthDataCache.get(cacheKey);
      if (cached) {
        const cachedMinPrice = cached.minPrice ?? deriveMinPrice(cached.days);
        const cachedResult: CalendarResponse = {
          success: true,
          data: { year, month, days: cached.days, roomName: calendarData.data?.roomName || '', roomTypeName: calendarData.data?.roomTypeName || null, currency: calendarData.data?.currency || 'EUR', minPrice: cachedMinPrice },
        };
        setCalendarData(cachedResult);
        onCalendarDataChange?.(cached.days);
        onPricesLoaded?.(cachedMinPrice, cachedResult.data!.currency);
        return;
      }

      // Cancel any pending fetch
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      try {
        const response = await fetch(
          `/api/calendar/${roomSlug}?year=${year}&month=${month}`,
          { signal: controller.signal }
        );
        const result: CalendarResponse = await response.json();

        if (result.success && result.data) {
          setCalendarData(result);

          // Update cache
          const key = getMonthKeyFromParts(year, month);
          setMonthDataCache((prev) => {
            const next = new Map(prev);
            next.set(key, { days: result.data!.days, minPrice: result.data!.minPrice ?? null });
            return next;
          });

          // Notify parent of new calendar data
          onCalendarDataChange?.(result.data.days);

          // Notify parent of new prices — server-emitted min already
          // includes the next-month fallback when the displayed month
          // has no usable price; drop the `available` filter on the
          // local fallback for the same reason (rate-card prices are
          // independent of bookings).
          const min = result.data.minPrice ?? deriveMinPrice(result.data.days);
          onPricesLoaded?.(min, result.data.currency);
        } else {
          onError?.();
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        log.error('availability_calendar.fetch.failed', { roomSlug, year, month, err });
        onError?.();
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [roomSlug, monthDataCache, calendarData.data?.roomName, calendarData.data?.roomTypeName, calendarData.data?.currency, onPricesLoaded, onError, onCalendarDataChange]
  );

  // Navigate to previous month — always responsive, update label immediately
  const goToPreviousMonth = useCallback(() => {
    if (!canNavigateToPrevious) return;
    const newMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const newYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    fetchCalendarData(newYear, newMonth);
  }, [canNavigateToPrevious, currentMonth, currentYear, fetchCalendarData]);

  // Navigate to next month — always responsive, update label immediately
  const goToNextMonth = useCallback(() => {
    const newMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const newYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    fetchCalendarData(newYear, newMonth);
  }, [currentMonth, currentYear, fetchCalendarData]);

  // Navigate to check-in month if interval is pre-filled (e.g. from URL params)
  const didInitialNav = useRef(false);
  useEffect(() => {
    if (didInitialNav.current || !interval.checkIn) return;
    didInitialNav.current = true;
    const y = parseInt(interval.checkIn.substring(0, 4));
    const m = parseInt(interval.checkIn.substring(5, 7));
    if (y !== currentYear || m !== currentMonth) {
      setCurrentYear(y);
      setCurrentMonth(m);
      fetchCalendarData(y, m);
    }
  }, [interval.checkIn, currentYear, currentMonth, fetchCalendarData]);

  // Generate calendar grid with empty cells for days before month start
  const calendarGrid = useMemo(() => {
    if (!calendarData.data) return [];

    const year = calendarData.data.year;
    const month = calendarData.data.month - 1;

    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const emptyCells: (CalendarDay | null)[] = Array.from(
      { length: startingDayOfWeek },
      () => null
    );

    return [...emptyCells, ...calendarData.data.days];
  }, [calendarData]);

  // Handle day click — booking.com-style 3-click cycle adapted for the 1-night default:
  //   Click 1 → set check-in + check-out = check-in+1 (phase → one-night)
  //   Click 2 (later) → extend check-out (phase → multi-night)
  //   Click 2 (at/before) → restart with 1-night on clicked (stays one-night)
  //   Click 3 (any) → restart with 1-night on clicked (phase → one-night)
  const handleDayClick = useCallback(
    (day: CalendarDay) => {
      if (day.isPast) return;

      const flash = (dates: string[]) => {
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        setFlashDates(new Set(dates));
        flashTimeoutRef.current = setTimeout(() => setFlashDates(new Set()), 1500);
      };

      const { checkIn } = interval;

      // Min-stay-aware first selection (doc 81 §12.3): clicking an available
      // check-in auto-fills check-out to checkIn + its min-stay (1 night on
      // normal days, e.g. 3 on a festive day). Falls back to a 1-night default
      // when the min-stay auto-range would cross a blocked night — a dead-end
      // arrival (§12.5), left to today's behaviour in v1.
      const startSelection = (d: CalendarDay) => {
        const autoCheckOut = addDays(d.date, d.minStay);
        const co =
          d.minStay > 1 && getBlockedDatesInRange(d.date, autoCheckOut).length > 0
            ? addOneDay(d.date)
            : autoCheckOut;
        onIntervalChange({ checkIn: d.date, checkOut: co });
        setPhase('one-night');
      };

      // Extend branch — only in one-night phase, only for a strictly-later click.
      if (phase === 'one-night' && checkIn && compareDates(day.date, checkIn) > 0) {
        const minStay = findDayData(checkIn)?.minStay ?? 1;
        const minCheckOut = addDays(checkIn, minStay);
        // Snap a below-minimum pick up to the min-stay floor (doc 81 §12.3).
        const targetCheckOut = compareDates(day.date, minCheckOut) < 0 ? minCheckOut : day.date;
        const blocked = getBlockedDatesInRange(checkIn, targetCheckOut);
        if (blocked.length > 0) {
          if (day.available) {
            startSelection(day);
          } else {
            flash([...blocked, day.date]);
            onIntervalChange({ checkIn: null, checkOut: null });
            setPhase('idle');
          }
          return;
        }
        onIntervalChange({ checkIn, checkOut: targetCheckOut });
        setPhase('multi-night');
        return;
      }

      // Restart branch — idle, multi-night (any click), or one-night with click at/before check-in.
      if (!day.available) {
        flash([day.date]);
        // Clear an already-committed multi-night on unavailable click (matches old click-3 UX).
        // For one-night phase, preserve the current 1-night default (matches old click-2 UX).
        if (phase === 'multi-night') {
          onIntervalChange({ checkIn: null, checkOut: null });
          setPhase('idle');
        }
        return;
      }
      startSelection(day);
    },
    [interval, phase, onIntervalChange, getBlockedDatesInRange, findDayData]
  );

  // Handle day hover for preview
  const handleDayHover = useCallback(
    (day: CalendarDay | null) => {
      if (selectionMode === 'check-out' && interval.checkIn) {
        setHoveredDate(day?.date || null);
      } else {
        setHoveredDate(null);
      }
    },
    [selectionMode, interval.checkIn]
  );

  // Min-stay floor for the active check-in during the check-out phase: the
  // earliest valid check-out (checkIn + its min-stay). null when no festive
  // minimum applies. Days strictly between checkIn and this floor are dimmed
  // and not previewable as check-out (doc 81 §12.3).
  const minStayFloorDate = useMemo(() => {
    if (phase !== 'one-night' || !interval.checkIn) return null;
    const ms = findDayData(interval.checkIn)?.minStay ?? 1;
    return ms > 1 ? addDays(interval.checkIn, ms) : null;
  }, [phase, interval.checkIn, findDayData]);

  // Min-stay nights for the selected/hovered check-in, for the notice banner.
  const activeMinStay = useMemo(() => {
    const anchor = interval.checkIn ?? hoveredDate;
    if (!anchor) return 1;
    return findDayData(anchor)?.minStay ?? 1;
  }, [interval.checkIn, hoveredDate, findDayData]);

  // Determine day visual state
  const getDayState = useCallback(
    (day: CalendarDay): 'check-in' | 'check-out' | 'in-range' | 'preview' | 'normal' => {
      const { checkIn, checkOut } = interval;

      if (checkIn && day.date === checkIn) return 'check-in';
      if (checkOut && day.date === checkOut) return 'check-out';
      if (checkIn && checkOut && isDateBetween(day.date, checkIn, checkOut)) return 'in-range';

      // Preview state during hover (check-out selection mode)
      // Only show preview when the hovered range has no unavailable nights and
      // the hovered check-out is at/after the min-stay floor.
      if (
        selectionMode === 'check-out' &&
        checkIn &&
        hoveredDate &&
        compareDates(hoveredDate, checkIn) > 0 &&
        (!minStayFloorDate || compareDates(hoveredDate, minStayFloorDate) >= 0) &&
        getBlockedDatesInRange(checkIn, hoveredDate).length === 0
      ) {
        if (isDateInRange(day.date, checkIn, hoveredDate) && day.date !== checkIn) {
          return 'preview';
        }
        if (day.date === hoveredDate) {
          return 'preview';
        }
      }

      return 'normal';
    },
    [interval, selectionMode, hoveredDate, getBlockedDatesInRange, minStayFloorDate]
  );

  // Get day number from date string
  const getDayNumber = (dateStr: string) => {
    return parseInt(dateStr.split('-')[2], 10);
  };

  return (
    <div
      className="flex flex-col rounded-xl pt-2 md:pt-3 min-w-[240px] w-72 md:w-96 overflow-visible relative"
      style={{ textShadow: '2px 2px 13px black' }}
    >
      {/* Header with navigation — always interactive */}
      <div className="flex justify-between items-center mb-1 md:mb-1.5 pb-1 md:pb-1.5 border-b border-white/45">
        <button
          className={cn(
            'p-1 rounded text-white transition-colors duration-200',
            canNavigateToPrevious
              ? 'cursor-pointer hover:bg-white/10'
              : 'opacity-0 pointer-events-none cursor-default'
          )}
          onClick={goToPreviousMonth}
          aria-label="Previous month"
        >
          <IoChevronBack size={18} />
        </button>
        <span className="text-white text-base md:text-lg font-semibold backdrop-blur-[1px]" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
          {t(`calendar.months.${MONTH_KEYS[currentMonth - 1]}`)} {currentYear}
        </span>
        <button
          className="p-1 rounded text-white cursor-pointer transition-colors duration-200 hover:bg-white/10"
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <IoChevronForward size={18} />
        </button>
      </div>

      {/* Festive min-stay hint (D31) */}
      <MinStayNotice nights={activeMinStay} tone="dark" />

      {/* Calendar grid — loading scoped to this area only */}
      <div className="relative min-h-[150px] md:min-h-[200px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-lg z-10">
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <div className="grid grid-cols-7 gap-0">
        {calendarGrid.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="opacity-0" />;
          }

          const dayNumber = getDayNumber(day.date);
          // All non-past days are clickable (unavailable days trigger flash feedback)
          const isClickable = !day.isPast;
          const hasBestRate = isBestRate(day);
          // Festive days (mirrors best-rate gating: only on bookable, non-past days).
          const hasFestive = day.isFestive && day.available && !day.isPast;
          const dayState = getDayState(day);
          // Dim days inside the min-stay floor (too short to be a check-out).
          const belowMinStay =
            minStayFloorDate !== null &&
            dayState === 'normal' &&
            interval.checkIn !== null &&
            compareDates(day.date, interval.checkIn) > 0 &&
            compareDates(day.date, minStayFloorDate) < 0;

          return (
            <button
              key={day.date}
              onClick={() => handleDayClick(day)}
              onMouseEnter={() => handleDayHover(day)}
              onMouseLeave={() => handleDayHover(null)}
              disabled={!isClickable}
              className={cn(
                'relative flex items-center justify-center min-h-[28px] md:min-h-[36px] text-base md:text-lg backdrop-blur-[1px] transition-colors duration-150',
                // Base state
                'text-white',
                // Past days - fully greyed, not clickable
                day.isPast && 'opacity-20 cursor-not-allowed',
                // Unavailable but not past - greyed out, unless actively selected
                // (an unavailable day is a valid check-out: guest leaves morning, next arrives afternoon)
                !day.available && !day.isPast && dayState === 'normal' && 'opacity-40',
                // Clickable state (cursor and hover)
                isClickable && 'cursor-pointer hover:bg-white/10',
                !isClickable && 'cursor-not-allowed',
                // Best rate state (only when not in selection; festive takes precedence)
                hasBestRate && !hasFestive && dayState === 'normal' && 'text-[#86c98a] font-semibold hover:bg-[#86c98a]/10',
                // Festive rate state (only when not in selection)
                hasFestive && dayState === 'normal' && 'text-[#c79a3a] font-semibold hover:bg-[#c79a3a]/10',
                // Check-in state
                dayState === 'check-in' && 'bg-white/30 ring-2 ring-white rounded-l-md z-10',
                // Check-out state
                dayState === 'check-out' && 'bg-white/30 ring-2 ring-white rounded-r-md z-10',
                // In-range state
                dayState === 'in-range' && 'bg-white/15',
                // Preview state (hover)
                dayState === 'preview' && 'bg-white/10',
                // Today indicator (only when not selected)
                day.isToday && dayState === 'normal' && 'ring-1 ring-amber-500/50',
                // Rounded corners for single-day selection
                dayState !== 'check-in' && dayState !== 'check-out' && dayState !== 'in-range' && dayState !== 'preview' && 'rounded-sm',
                // Below the min-stay floor: dimmed, can't be a check-out
                belowMinStay && 'opacity-30',
                // Flash blocked dates red
                flashDates.has(day.date) && 'animate-flash-blocked ring-2 ring-red-500 bg-red-500/30 opacity-100'
              )}
              style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}
              title={
                hasFestive && dayState === 'normal'
                  ? 'Festive Rate'
                  : hasBestRate && dayState === 'normal'
                    ? 'Best Rate'
                    : undefined
              }
            >
              {dayNumber}
              {/* Day markers stack in a column at the top-right so they never
                  overlap: rate indicator (% best-rate / ✦ festive) on top, the
                  red min-stay "!" beneath it. %/✦ are mutually exclusive, and "!"
                  is suppressed on festive days (the gold star already covers them). */}
              {dayState === 'normal' && (
                <span className="absolute top-0 right-0.5 z-10 flex flex-col items-end leading-none">
                  {hasBestRate && !hasFestive && (
                    <span className="text-[10px] leading-none text-[#86c98a]/90">%</span>
                  )}
                  {hasFestive && (
                    <span className="text-[10px] leading-none text-[#c79a3a]/90">✦</span>
                  )}
                  {day.minStay > 1 && !hasFestive && (
                    <span className="text-[10px] font-bold leading-none text-red-400" title={`Minimum ${day.minStay} nights`}>!</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>

      {/* Error message */}
      {!calendarData.success && calendarData.fallbackMessage && (
        <div className="text-white/60 text-sm text-center py-4">
          {calendarData.fallbackMessage}
        </div>
      )}
    </div>
  );
}
